import { useCallback, useMemo, useState } from 'react';
import { useGetPvxPartnersQuery } from '@api/pvxPartners';
import { PVX_PARTNERS_TYPE } from '@api/types/pvxPartners';
import { CLIENT_USER_ROLE } from '@api/types/client-user-management';
import { Row } from '../../../../components/GenericTable/GenericTable';
import { mapPvxPartnersToTableRows } from '../utils/mapPvxPartnersToTableRows';

export type PvxPartnerTableRowType = 'company' | 'user';

export type PvxPartnerTableRow = {
  id: string;
  rowType: PvxPartnerTableRowType;
  company: string;
  user: string;
  role: CLIENT_USER_ROLE | null;
  dashboards: string;
  dashboardSubmoduleNames: string[];
  totalEntitlementCount: number;
  hasFullDashboardAccess: boolean;
  joiningDate: string;
  status: string;
  clientCode?: string;
  membershipGrantId?: string;
  userId?: string;
  partnerType: PVX_PARTNERS_TYPE;
};

type UsePvxPartnersTableParams = {
  pageNumber: number;
  pageSize: number;
  searchQuery: string;
  onPageReset?: () => void;
};

export const usePvxPartnersTable = ({
  pageNumber,
  pageSize,
  searchQuery,
  onPageReset,
}: UsePvxPartnersTableParams) => {
  const [sort, setSort] = useState('joiningDate');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');

  const { data, isLoading, isFetching, refetch } = useGetPvxPartnersQuery({
    pageNumber: pageNumber,
    pageSize,
    search: searchQuery,
    sort,
    sortOrder,
    role: roleFilter ? [roleFilter as CLIENT_USER_ROLE] : undefined,
    status: statusFilter ? [statusFilter] : undefined,
    partnerType: typeFilter ? [typeFilter as PVX_PARTNERS_TYPE] : undefined,
  });

  const rows = useMemo<Row<PvxPartnerTableRow>[]>(
    () => mapPvxPartnersToTableRows(data?.data.items ?? []),
    [data],
  );

  const total = data?.data.totalElements ?? 0;

  const handleSort = useCallback(
    (sortKey: string) => {
      if (sortKey === sort) {
        setSortOrder((previous) => (previous === 'ASC' ? 'DESC' : 'ASC'));
      } else {
        setSort(sortKey);
        setSortOrder('ASC');
      }
      onPageReset?.();
    },
    [sort, onPageReset],
  );

  const handleRoleFilterChange = useCallback(
    (filter: string) => {
      setRoleFilter(filter);
      onPageReset?.();
    },
    [onPageReset],
  );

  const handleStatusFilterChange = useCallback(
    (filter: string) => {
      setStatusFilter(filter);
      onPageReset?.();
    },
    [onPageReset],
  );

  const handleTypeFilterChange = useCallback(
    (filter: string) => {
      setTypeFilter(filter);
      onPageReset?.();
    },
    [onPageReset],
  );

  return {
    rows,
    isLoading: isLoading || isFetching,
    total,
    sort,
    sortOrder,
    handleSort,
    roleFilter,
    handleRoleFilterChange,
    statusFilter,
    handleStatusFilterChange,
    typeFilter,
    handleTypeFilterChange,
    refetch,
  };
};
